import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Card from '../components/Card';
import Button from '../components/Button';
import { inputStyle } from '../components/FormField';
import * as dispatchApi from '../services/dispatchAnalysisApi';
import SalesMap from '../components/SalesMap';
import CustomerTrackingCard from '../components/CustomerTrackingCard';
import CustomerHistoryModal from '../components/CustomerHistoryModal';
import GlobalSearchBar from '../components/GlobalSearchBar';
import { formatDate } from '../utils/dateFormat';
import DateInput from '../components/DateInput';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 1 -> "1st", 2 -> "2nd", 3 -> "3rd", 11-13 -> "th" (the exception), else "th". */
function ordinalSuffix(day) {
  if (day >= 11 && day <= 13) return `${day}th`;
  const lastDigit = day % 10;
  const suffix = lastDigit === 1 ? 'st' : lastDigit === 2 ? 'nd' : lastDigit === 3 ? 'rd' : 'th';
  return `${day}${suffix}`;
}

function formatDDMMYYYY(dateString) {
  const d = new Date(dateString);
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

/** True only for the real-world current calendar month (today's actual
 * year+month) — a past month's data is treated as settled/complete, so the
 * "till [latest date]" indicator only makes sense for the month that's
 * still actively receiving new dispatch data right now. */
function isCurrentRunningMonth(year, month) {
  const now = new Date();
  return year === now.getFullYear() && month === now.getMonth() + 1;
}

const DIMENSIONS = [
  { key: 'customer', label: 'Customer' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'group', label: 'Group' },
  { key: 'refParty', label: 'Ref. Party' },
  { key: 'design', label: 'Design' },
  { key: 'category', label: 'Category' },
];

// Order matches the request exactly: Ref. Party, Category, State, City,
// Group, Design, Customer. Reused as-is for the Comparison feature's
// dimension picker, so both places offer exactly the same 7 options.
const DRILLDOWN_DIMENSIONS = [
  { key: 'refParty', label: 'Ref. Party-wise Dispatch' },
  { key: 'category', label: 'Category-wise Dispatch' },
  { key: 'state', label: 'State-wise Dispatch' },
  { key: 'city', label: 'City-wise Dispatch' },
  { key: 'group', label: 'Group-wise Dispatch' },
  { key: 'design', label: 'Design-wise Dispatch' },
  { key: 'customer', label: 'Customer-wise Dispatch' },
];

function DrilldownCard({ label, sublabel, onClick, active }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? 'var(--color-accent-soft)' : 'var(--color-surface)',
        border: active ? '1px solid var(--color-accent)' : '1px solid var(--color-border)',
        borderRadius: 'var(--radius-sm)',
        padding: 'var(--space-3) var(--space-4)',
        minWidth: 140,
        cursor: 'pointer',
        textAlign: 'left',
        font: 'inherit',
      }}
    >
      <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: active ? 'var(--color-accent)' : 'var(--color-text-primary)' }}>
        {label}
      </div>
      {sublabel !== undefined && (
        <div className="data-figure" style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, marginTop: 4 }}>{sublabel}</div>
      )}
    </button>
  );
}

function SortableHeader({ label, sortKey, activeSort, onSort }) {
  const isActive = activeSort.key === sortKey;
  const arrow = isActive ? (activeSort.direction === 'asc' ? ' \u2191' : ' \u2193') : '';
  return (
    <th
      role="button"
      tabIndex={0}
      onClick={() => onSort(sortKey)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSort(sortKey); }}
      style={{
        padding: '8px 4px',
        fontSize: 'var(--font-size-xs)',
        color: isActive ? 'var(--color-accent)' : 'var(--color-text-muted)',
        cursor: 'pointer',
        userSelect: 'none',
        fontWeight: isActive ? 700 : 400,
        verticalAlign: 'bottom',
      }}
    >
      {/* A plain-auto table sizes columns to fit content and ignores
          width/maxWidth set directly on the <th> — this inner div's fixed
          width is what actually forces "600X1200 All Over %" etc. onto
          2-3 lines instead of stretching the column to one long line. */}
      <div style={{ width: 78, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.3 }}>
        {label}{arrow}
      </div>
    </th>
  );
}

function OnTimeBadge({ pct }) {
  if (pct === null) return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
  const color = pct >= 80 ? 'var(--color-success)' : pct >= 50 ? 'var(--color-warning)' : 'var(--color-danger)';
  return <span className="data-figure" style={{ color, fontWeight: 600 }}>{pct}%</span>;
}

/** A clickable count — the four Active/New/Total/Inactive Customer numbers
 * on the Ref. Party-wise table — opening a small modal listing exactly who
 * those customers are, via onOpen(customers, title). */
function ClickableCount({ value, customers, title, onOpen, color }) {
  if (!customers || customers.length === 0) {
    return <span className="data-figure" style={{ color: color || 'var(--color-text-primary)' }}>{value}</span>;
  }
  return (
    <button
      type="button"
      onClick={() => onOpen(customers, title)}
      className="data-figure"
      style={{
        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        color: color || 'var(--color-accent)', fontWeight: 600, textDecoration: 'underline',
        font: 'inherit',
      }}
    >
      {value}
    </button>
  );
}

/** The modal itself — a simple centered overlay listing customer names,
 * closable by the X button or clicking outside. */
function CustomerListModal({ title, customers, onClose }) {
  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div
        role="dialog"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-surface)', borderRadius: 'var(--radius-md)',
          padding: 'var(--space-5)', maxWidth: 420, width: '90%', maxHeight: '70vh',
          overflowY: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
          <h3 style={{ margin: 0, fontSize: 'var(--font-size-md)' }}>{title}</h3>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
            ✕
          </button>
        </div>
        {customers.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>No customers in this list.</p>
        ) : (
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            {customers.map((c) => (
              <li key={c.id} style={{ fontSize: 'var(--font-size-sm)', padding: '4px 0' }}>{c.name}</li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

/** A selected comparison period's own unique key — "2025" for a year-only
 * period, "2025-3" for a year+month one — used to prevent adding the same
 * period twice and to React-key the period chips. */
function periodKey(p) {
  return p.month ? `${p.year}-${p.month}` : `${p.year}`;
}

/** Display label for one comparison period — "2026" or "Mar 2026". */
function periodLabel(p) {
  return p.month ? `${MONTH_NAMES[p.month - 1]} ${p.year}` : `${p.year}`;
}

// A distinct color per period, cycling if more than 6 are selected — used
// as a small dot on each period's chip and a matching accent line under
// its column header, so two periods sitting side by side in the same
// metric group (e.g. "2025" next to "2026" under one size's All Over %)
// read as visually separate at a glance, not just as adjacent numbers.
const PERIOD_COLORS = ['#2563eb', '#d97706', '#16a34a', '#9333ea', '#dc2626', '#0891b2'];
function periodColor(index) {
  return PERIOD_COLORS[index % PERIOD_COLORS.length];
}

export default function DispatchAnalysisPage() {
  const [delayed, setDelayed] = useState([]);
  const [dimension, setDimension] = useState('customer');
  const [breakdown, setBreakdown] = useState([]);
  const [grades, setGrades] = useState(null);
  const [dayOfWeek, setDayOfWeek] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Year -> Month -> dimension drill-down state.
  const [years, setYears] = useState([]);
  const [yearsError, setYearsError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDimension, setSelectedDimension] = useState(null);
  const [dimensionDetail, setDimensionDetail] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [yearsLoading, setYearsLoading] = useState(true);
  const [mapOpenForYear, setMapOpenForYear] = useState(null);
  const [mapOpenForMonth, setMapOpenForMonth] = useState(null);
  const monthName = selectedMonth ? MONTH_NAMES[selectedMonth - 1] : '';

  // Comparison feature state. comparisonMode is null until "Year-wise
  // Comparison" / "Month-wise Comparison" is picked; comparisonPeriods
  // builds up via the round "+" button (each entry {year} or
  // {year, month} depending on the mode); comparisonDimension is one of
  // the 7 DRILLDOWN_DIMENSIONS, chosen once at least 2 periods exist;
  // comparisonData holds the fetched table, cleared whenever the period
  // list or the chosen dimension changes so a stale table never lingers.
  const [comparisonMode, setComparisonMode] = useState(null);
  const [comparisonPeriods, setComparisonPeriods] = useState([]);
  const [comparisonDropdownOpen, setComparisonDropdownOpen] = useState(false);
  const [comparisonDropdownYear, setComparisonDropdownYear] = useState('');
  const [comparisonDropdownMonth, setComparisonDropdownMonth] = useState('');
  const [comparisonDropdownMonths, setComparisonDropdownMonths] = useState([]);
  const [comparisonDimension, setComparisonDimension] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonError, setComparisonError] = useState(null);

  useEffect(() => {
    setYearsLoading(true);
    dispatchApi.getYears()
      .then((data) => { setYears(data); setYearsError(null); })
      .catch((err) => setYearsError(err.message || 'Could not load years.'))
      .finally(() => setYearsLoading(false));
  }, []);

  const [monthsError, setMonthsError] = useState(null);
  const openYear = (year) => {
    // Clicking the already-open year again closes its months/breakdowns.
    if (selectedYear === year) {
      setSelectedYear(null);
      setSelectedMonth(null);
      setSelectedDimension(null);
      setDimensionDetail(null);
      setMapOpenForMonth(null);
      return;
    }
    setSelectedYear(year);
    setSelectedMonth(null);
    setSelectedDimension(null);
    setDimensionDetail(null);
    setMapOpenForMonth(null);
    dispatchApi.getMonths(year)
      .then((data) => { setMonths(data); setMonthsError(null); })
      .catch((err) => setMonthsError(err.message || 'Could not load months.'));
  };

  const openMonth = (month) => {
    setSelectedMonth(month);
    setSelectedDimension(null);
    setDimensionDetail(null);
  };

  const [detailSort, setDetailSort] = useState({ key: null, direction: 'asc' });
  const [customerListModal, setCustomerListModal] = useState(null);
  const openCustomerList = (customers, title) => setCustomerListModal({ customers, title });
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const openInactiveCustomers = (customers, title) => navigate('/inactive-customers', { state: { customers, title } });
  const [detailSearch, setDetailSearch] = useState('');
  const [historyCustomer, setHistoryCustomer] = useState(null);
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const [rangeLoading, setRangeLoading] = useState(false);
  const rangeSeq = React.useRef(0);
  const rangeActive = selectedDimension === 'customer' && (rangeFrom !== '' || rangeTo !== '');
  // Re-fetch the table for a custom From-To date range (empty both = back to the selected month).
  const applyRange = (from, to) => {
    setRangeFrom(from);
    setRangeTo(to);
    if (!selectedDimension) return;
    rangeSeq.current += 1;
    const seq = rangeSeq.current;
    setRangeLoading(true);
    dispatchApi.getDimensionDetail(selectedYear, selectedMonth, selectedDimension, { from, to })
      .then((d) => { if (seq === rangeSeq.current) setDimensionDetail(d); })
      .catch(() => { if (seq === rangeSeq.current) setDimensionDetail(null); })
      .finally(() => { if (seq === rangeSeq.current) setRangeLoading(false); });
  };
  const searchActive = selectedDimension === 'customer' && detailSearch.trim() !== '';
  const matchesSearch = (row) => !searchActive || String(row.name || '').toLowerCase().includes(detailSearch.trim().toLowerCase());

  const openDimension = (dim) => {
    setSelectedDimension(dim);
    setDrilldownLoading(true);
    setDetailSort({ key: null, direction: 'asc' });
    setDetailSearch('');
    setRangeFrom('');
    setRangeTo('');
    rangeSeq.current += 1;
    dispatchApi.getDimensionDetail(selectedYear, selectedMonth, dim)
      .then(setDimensionDetail)
      .catch(() => setDimensionDetail(null))
      .finally(() => setDrilldownLoading(false));
  };

  // Arrived from the Dashboard (e.g. the "Dispatched This Month" card): open that period's breakdown directly.
  const autoOpen = routerLocation.state && routerLocation.state.autoOpen;
  useEffect(() => {
    if (!autoOpen) return;
    const { year, month, dimension } = autoOpen;
    setSelectedYear(year);
    setSelectedMonth(month);
    setSelectedDimension(dimension);
    setDimensionDetail(null);
    setMapOpenForMonth(null);
    setDetailSort({ key: null, direction: 'asc' });
    setDetailSearch('');
    setRangeFrom('');
    setRangeTo('');
    setDrilldownLoading(true);
    rangeSeq.current += 1;
    dispatchApi.getMonths(year).then((d) => { setMonths(d); setMonthsError(null); }).catch(() => {});
    dispatchApi.getDimensionDetail(year, month, dimension)
      .then(setDimensionDetail)
      .catch(() => setDimensionDetail(null))
      .finally(() => setDrilldownLoading(false));
  }, [autoOpen]);

  const focusMode = Boolean(autoOpen);
  const handleDetailSort = (key) => {
    setDetailSort((prev) => (prev.key === key
      ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: 'asc' }));
  };

  // Size columns are dynamic per month (whatever sizes appear in that
  // month's dispatches), so their sort keys are built as "size:<name>" /
  // "allover:<name>" and resolved here rather than being fixed fields —
  // everything else (name, totals, on-time %, avg delay) is a normal field.
  const getDetailSortValue = (row, key) => {
    if (key === 'name') return row.name;
    if (key.startsWith('size:')) return row.sizes[key.slice(5)].qty;
    if (key.startsWith('allover:')) return row.sizes[key.slice(8)].allOverPct;
    return row[key];
  };

  const sortedDetailRows = React.useMemo(() => {
    if (!dimensionDetail || !detailSort.key) return dimensionDetail ? dimensionDetail.rows : [];
    const copy = [...dimensionDetail.rows];
    copy.sort((a, b) => {
      const av = getDetailSortValue(a, detailSort.key);
      const bv = getDetailSortValue(b, detailSort.key);
      if (detailSort.key === 'name') {
        const cmp = (av || '').toString().localeCompare((bv || '').toString(), undefined, { numeric: true, sensitivity: 'base' });
        return detailSort.direction === 'asc' ? cmp : -cmp;
      }
      const an = Number(av) || 0;
      const bn = Number(bv) || 0;
      return detailSort.direction === 'asc' ? an - bn : bn - an;
    });
    return copy;
  }, [dimensionDetail, detailSort]);

  const detailTotals = React.useMemo(() => {
    if (!dimensionDetail || dimensionDetail.rows.length === 0) return null;
    const sizeQty = {};
    dimensionDetail.sizes.forEach((s) => { sizeQty[s] = 0; });
    let totalQty = 0;
    let dispatchCount = 0;
    // CUST-TOTALS v1: totals of the four customer-count columns (Ref. Party-wise table)
    let oldCustomerCount = 0; let newCustomerCount = 0; let totalCustomerCount = 0; let inactiveCustomerCount = 0;
    // UNIQUE-TOTAL v1: a customer working with 2 Ref. Parties is counted twice above; these count each customer once
    const uOld = new Set(); const uNew = new Set(); const uIn = new Set();
    dimensionDetail.rows.forEach((r) => {
      dimensionDetail.sizes.forEach((s) => { sizeQty[s] += Number(r.sizes[s].qty) || 0; });
      totalQty += Number(r.totalQty) || 0;
      dispatchCount += Number(r.dispatchCount) || 0;
      oldCustomerCount += Number(r.oldCustomerCount) || 0;
      newCustomerCount += Number(r.newCustomerCount) || 0;
      totalCustomerCount += Number(r.totalCustomerCount) || 0;
      inactiveCustomerCount += Number(r.inactiveCustomerCount) || 0;
      (r.activeCustomers || []).forEach((c) => uOld.add(c.id));
      (r.newCustomers || []).forEach((c) => uNew.add(c.id));
      (r.inactiveCustomers || []).forEach((c) => uIn.add(c.id));
    });
    // Total = Active + New only when that is how every row's Total is made; otherwise it is not shown
    const totalIsSum = dimensionDetail.rows.every((r) => Number(r.totalCustomerCount) === (Number(r.oldCustomerCount) || 0) + (Number(r.newCustomerCount) || 0));
    const unique = { old: uOld.size, nw: uNew.size, inactive: uIn.size, total: totalIsSum ? new Set([...uOld, ...uNew]).size : null };
    return {
      sizeQty, totalQty, dispatchCount, oldCustomerCount, newCustomerCount, totalCustomerCount, inactiveCustomerCount, unique,
    };
  }, [dimensionDetail]);

  // --- Comparison feature handlers ---

  const openComparisonMode = (mode) => {
    setComparisonMode(mode);
    setComparisonPeriods([]);
    setComparisonDropdownOpen(false);
    setComparisonDropdownYear('');
    setComparisonDropdownMonth('');
    setComparisonDropdownMonths([]);
    setComparisonDimension(null);
    setComparisonData(null);
    setComparisonError(null);
  };

  const toggleComparisonDropdown = () => {
    setComparisonDropdownOpen((v) => !v);
    setComparisonDropdownYear('');
    setComparisonDropdownMonth('');
    setComparisonDropdownMonths([]);
  };

  const handleComparisonDropdownYearChange = (y) => {
    setComparisonDropdownYear(y);
    setComparisonDropdownMonth('');
    if (comparisonMode === 'month' && y) {
      dispatchApi.getMonths(Number(y)).then(setComparisonDropdownMonths).catch(() => setComparisonDropdownMonths([]));
    }
  };

  const addComparisonPeriod = () => {
    if (!comparisonDropdownYear) return;
    if (comparisonMode === 'month' && !comparisonDropdownMonth) return;
    const newPeriod = comparisonMode === 'month'
      ? { year: Number(comparisonDropdownYear), month: Number(comparisonDropdownMonth) }
      : { year: Number(comparisonDropdownYear) };
    const key = periodKey(newPeriod);
    if (!comparisonPeriods.some((p) => periodKey(p) === key)) {
      setComparisonPeriods((prev) => [...prev, newPeriod]);
      setComparisonDimension(null);
      setComparisonData(null);
    }
    setComparisonDropdownOpen(false);
  };

  const removeComparisonPeriod = (key) => {
    setComparisonPeriods((prev) => prev.filter((p) => periodKey(p) !== key));
    setComparisonDimension(null);
    setComparisonData(null);
  };

  // Clicking a dimension card runs the comparison immediately — dim is
  // passed directly into the fetch rather than relying on
  // comparisonDimension state, since setState is async and the fetch
  // would otherwise fire with the previous (stale) selection.
  const openComparisonDimension = (dim) => {
    setComparisonDimension(dim);
    setComparisonData(null);
    setComparisonError(null);
    if (comparisonPeriods.length < 2) return;
    setComparisonLoading(true);
    dispatchApi.getComparison(dim, comparisonPeriods)
      .then(setComparisonData)
      .catch((err) => setComparisonError(err.message || 'Could not load comparison.'))
      .finally(() => setComparisonLoading(false));
  };

  const loadAll = async () => {
    const [d, g, w] = await Promise.all([
      dispatchApi.listDelayed(),
      dispatchApi.getGrades(),
      dispatchApi.getDayOfWeek(),
    ]);
    setDelayed(d);
    setGrades(g);
    setDayOfWeek(w);
  };

  useEffect(() => {
    setLoading(true);
    loadAll()
      .catch((err) => setError(err.message || 'Could not load dispatch analysis.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    dispatchApi.getBreakdown(dimension).then(setBreakdown).catch(() => {});
  }, [dimension]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await dispatchApi.refreshDispatchAnalysis();
      await loadAll();
      await dispatchApi.getBreakdown(dimension).then(setBreakdown);
    } catch (err) {
      setError(err.message || 'Refresh failed.');
    } finally {
      setRefreshing(false);
    }
  };

  const maxDayQty = Math.max(1, ...dayOfWeek.map((d) => d.dispatchedQty));

  return (
    <div>
      {focusMode && (
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
          <button
            type="button"
            onClick={() => navigate('/')}
            style={{ padding: '6px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text-primary)', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}
          >
            ← Back to Dashboard
          </button>
          <button
            type="button"
            onClick={() => navigate('/dispatch-analysis', { replace: true })}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-accent)', font: 'inherit', fontSize: 'var(--font-size-sm)', textDecoration: 'underline' }}
          >
            Open full Dispatch Analysis
          </button>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>{focusMode ? 'Customer-wise Dispatch' : 'Dispatch Analysis'}</h1>
          <div style={{ maxWidth: 420, margin: '8px 0 12px' }}><GlobalSearchBar customersOnly /></div>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            Delay tracking, on-time performance, grade mix, and dispatch timing.
          </p>
        </div>
        <Button variant="secondary" onClick={handleRefresh} disabled={refreshing}>
          {refreshing ? 'Refreshing…' : 'Refresh analysis'}
        </Button>
      </div>

      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}

      <Card title={focusMode ? `Customer-wise Dispatch — ${MONTH_NAMES[autoOpen.month - 1]} ${autoOpen.year}` : 'Dispatch by Year'}>
        {!focusMode && (
          <>
        {yearsError && (
          <p style={{ color: 'var(--color-danger)', marginTop: 0 }}>
            Could not load dispatch years: {yearsError}
          </p>
        )}
        {monthsError && (
          <p style={{ color: 'var(--color-danger)' }}>
            Could not load months: {monthsError}
          </p>
        )}
        {yearsLoading ? (
          <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>
        ) : (
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: selectedYear ? 'var(--space-4)' : 0 }}>
            {years.map((y) => (
              <div key={y.year} style={{ display: 'flex', flexDirection: 'column' }}>
                <DrilldownCard label={String(y.year)} sublabel={y.totalQty} active={selectedYear === y.year} onClick={() => openYear(y.year)} />
                <button
                  type="button"
                  onClick={() => setMapOpenForYear(mapOpenForYear === y.year ? null : y.year)}
                  style={{
                    marginTop: 2,
                    padding: '3px 0',
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    textAlign: 'center',
                    background: mapOpenForYear === y.year ? 'var(--color-accent)' : 'var(--color-surface-alt)',
                    color: mapOpenForYear === y.year ? '#fff' : 'var(--color-text-muted)',
                    border: '1px solid var(--color-border)',
                    borderTop: 'none',
                    borderRadius: '0 0 var(--radius-sm) var(--radius-sm)',
                    cursor: 'pointer',
                  }}
                >
                  MAP
                </button>
              </div>
            ))}
            {years.length === 0 && !yearsError && <p style={{ color: 'var(--color-text-muted)' }}>No dispatch data yet.</p>}
          </div>
        )}

        {mapOpenForYear && (
          <div style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-4)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>Sales Map</h3>
              <button type="button" onClick={() => setMapOpenForYear(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                ✕ Close
              </button>
            </div>
            <SalesMap year={mapOpenForYear} />
          </div>
        )}

        {selectedYear && (
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', marginBottom: 8 }}>
              {selectedYear} — select a month
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
              {months.map((m) => (
                <div key={m.month} style={{ display: 'flex', flexDirection: 'column' }}>
                  <DrilldownCard
                    label={
                      isCurrentRunningMonth(selectedYear, m.month) && m.latestDate
                        ? `${m.monthName} (till ${ordinalSuffix(new Date(m.latestDate).getUTCDate())} updated dispatch)`
                        : m.monthName
                    }
                    sublabel={m.totalQty}
                    active={selectedMonth === m.month}
                    onClick={() => openMonth(m.month)}
                  />
                  <button
                    type="button"
                    onClick={() => setMapOpenForMonth(mapOpenForMonth === m.month ? null : m.month)}
                    style={{
                      marginTop: 2,
                      padding: '3px 0',
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: '0.05em',
                      textAlign: 'center',
                      background: mapOpenForMonth === m.month ? 'var(--color-accent)' : 'var(--color-surface-alt)',
                      color: mapOpenForMonth === m.month ? '#fff' : 'var(--color-text-muted)',
                      border: '1px solid var(--color-border)',
                      borderTop: 'none',
                      borderRadius: '0 0 var(--radius-sm) var(--radius-sm)',
                      cursor: 'pointer',
                    }}
                  >
                    MAP
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {mapOpenForMonth && (
          <div style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-4)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>Sales Map — {MONTH_NAMES[mapOpenForMonth - 1]} {selectedYear}</h3>
              <button type="button" onClick={() => setMapOpenForMonth(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                ✕ Close
              </button>
            </div>
            <SalesMap year={selectedYear} month={mapOpenForMonth} />
          </div>
        )}

        {selectedYear && (
          <div>
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', marginBottom: 8 }}>
              {(() => {
                const selectedMonthData = months.find((m) => m.month === selectedMonth);
                const label = isCurrentRunningMonth(selectedYear, selectedMonth) && selectedMonthData && selectedMonthData.latestDate
                  ? formatDDMMYYYY(selectedMonthData.latestDate)
                  : `${monthName} ${selectedYear}`;
                return `${selectedMonth ? label : `${selectedYear} (full year)`} — select a breakdown`;
              })()}
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
              {DRILLDOWN_DIMENSIONS.map((d) => (
                <DrilldownCard key={d.key} label={d.label} active={selectedDimension === d.key} onClick={() => openDimension(d.key)} />
              ))}
            </div>
          </div>
        )}

          </>
        )}
        {selectedDimension && (
          <div style={{ marginTop: 'var(--space-4)' }}>
            {drilldownLoading ? (
              <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>
            ) : dimensionDetail && dimensionDetail.rows.length > 0 ? (
              <>
              {selectedDimension === 'customer' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                  <input
                    type="search"
                    value={detailSearch}
                    onChange={(e) => setDetailSearch(e.target.value)}
                    placeholder="Search customer…"
                    aria-label="Search customer"
                    style={{ flex: '1 1 260px', maxWidth: 420, padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)' }}
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                    From
                    <DateInput
                      value={rangeFrom}
                      max={rangeTo || undefined}
                      onChange={(e) => applyRange(e.target.value, rangeTo)}
                      aria-label="From date"
                      style={{ padding: '7px 8px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)' }}
                    />
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                    To
                    <DateInput
                      value={rangeTo}
                      min={rangeFrom || undefined}
                      onChange={(e) => applyRange(rangeFrom, e.target.value)}
                      aria-label="To date"
                      style={{ padding: '7px 8px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)' }}
                    />
                  </label>
                  {rangeActive && (
                    <button type="button" onClick={() => applyRange('', '')} style={{ padding: '7px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', background: '#fff', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>
                      ✕ Clear dates
                    </button>
                  )}
                  {rangeLoading && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Updating…</span>}
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                    {searchActive
                      ? `${dimensionDetail.rows.filter(matchesSearch).length} of ${dimensionDetail.rows.length} customers`
                      : `${dimensionDetail.rows.length} customers — click a name for the full history`}
                  </span>
                </div>
              )}
              <div className="table-scroll table-freeze">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-border)' }}>
                      <th style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', whiteSpace: 'normal', maxWidth: 40, verticalAlign: 'bottom' }}>No.</th>
                      <SortableHeader
                        label={DRILLDOWN_DIMENSIONS.find((d) => d.key === selectedDimension).label.replace('-wise Dispatch', '')}
                        sortKey="name"
                        activeSort={detailSort}
                        onSort={handleDetailSort}
                      />
                      {dimensionDetail.sizes.map((sizeName) => (
                        <SortableHeader key={sizeName} label={sizeName} sortKey={`size:${sizeName}`} activeSort={detailSort} onSort={handleDetailSort} />
                      ))}
                      {dimensionDetail.sizes.map((sizeName) => (
                        <SortableHeader key={`h-allover-${sizeName}`} label={`${sizeName} All Over %`} sortKey={`allover:${sizeName}`} activeSort={detailSort} onSort={handleDetailSort} />
                      ))}
                      <SortableHeader label="Total Dispatch Qty" sortKey="totalQty" activeSort={detailSort} onSort={handleDetailSort} />
                      <SortableHeader label="Dispatches" sortKey="dispatchCount" activeSort={detailSort} onSort={handleDetailSort} />
                      <SortableHeader label="On-Time %" sortKey="onTimePct" activeSort={detailSort} onSort={handleDetailSort} />
                      <SortableHeader label="Avg Delay" sortKey="avgDelayDays" activeSort={detailSort} onSort={handleDetailSort} />
                      {selectedDimension === 'refParty' && (
                        <>
                          <SortableHeader label="No. of Active Customers" sortKey="oldCustomerCount" activeSort={detailSort} onSort={handleDetailSort} />
                          <SortableHeader label="No. of New Customers" sortKey="newCustomerCount" activeSort={detailSort} onSort={handleDetailSort} />
                          <SortableHeader label="Total Active Customers" sortKey="totalCustomerCount" activeSort={detailSort} onSort={handleDetailSort} />
                          <SortableHeader label="No. of Inactive Customers" sortKey="inactiveCustomerCount" activeSort={detailSort} onSort={handleDetailSort} />
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDetailRows.filter(matchesSearch).map((row) => (
                      <tr key={row.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                        <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{row.no}</td>
                        <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>
                          {selectedDimension === 'customer' ? (
                            <button
                              type="button"
                              onClick={() => setHistoryCustomer({ id: row.id, name: row.name })}
                              title="Open full dispatch history"
                              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--color-accent)', font: 'inherit', fontWeight: 600, textAlign: 'left', textDecoration: 'underline' }}
                            >
                              {row.name}
                            </button>
                          ) : (selectedDimension === 'refParty' && row.id ? (
                            <button
                              type="button"
                              onClick={() => setHistoryCustomer({ id: row.id, name: row.name, kind: 'refParty' })}
                              title="Open all dispatches of this Ref. Party"
                              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--color-accent)', font: 'inherit', fontWeight: 600, textAlign: 'left', textDecoration: 'underline' }}
                            >
                              {row.name}
                            </button>
                          ) : row.name)}
                        </td>
                        {dimensionDetail.sizes.map((sizeName) => (
                          <td key={sizeName} className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>
                            {row.sizes[sizeName].qty}
                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>({row.sizes[sizeName].pct}%)</div>
                          </td>
                        ))}
                        {dimensionDetail.sizes.map((sizeName) => (
                          <td key={`allover-${sizeName}`} className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>
                            {row.sizes[sizeName].allOverPct}%
                          </td>
                        ))}
                        <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>{row.totalQty}</td>
                        <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{row.dispatchCount}</td>
                        <td style={{ padding: '8px 4px' }}><OnTimeBadge pct={row.onTimePct} /></td>
                        <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>
                          {row.avgDelayDays === null ? '—' : `${row.avgDelayDays}d`}
                        </td>
                        {selectedDimension === 'refParty' && (
                          <>
                            <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>
                              <ClickableCount value={row.oldCustomerCount} customers={row.activeCustomers} title={`Active Customers — ${row.name}`} onOpen={openCustomerList} />
                            </td>
                            <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>
                              <ClickableCount value={row.newCustomerCount} customers={row.newCustomers} title={`New Customers — ${row.name}`} onOpen={openCustomerList} />
                            </td>
                            <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>
                              <ClickableCount value={row.totalCustomerCount} customers={row.totalCustomers} title={`Total Customers — ${row.name}`} onOpen={openCustomerList} color="var(--color-accent)" />
                            </td>
                            <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>
                              <ClickableCount value={row.inactiveCustomerCount} customers={row.inactiveCustomers} title={`Inactive Customers — ${row.name}`} onOpen={openInactiveCustomers} color="var(--color-text-muted)" />
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                    {detailTotals && !searchActive && (
                      <tr style={{ background: '#e2e8f0', borderTop: '2px solid var(--color-border)' }}>
                        <td style={{ padding: '8px 4px' }} />
                        <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b' }}>Grand Total</td>
                        {dimensionDetail.sizes.map((sz) => (
                          <td key={'gt-' + sz} className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b' }}>
                            {detailTotals.sizeQty[sz]}
                            <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 400, color: 'var(--color-text-muted)' }}>
                              ({detailTotals.totalQty > 0 ? Math.round((detailTotals.sizeQty[sz] / detailTotals.totalQty) * 1000) / 10 : 0}%)
                            </div>
                          </td>
                        ))}
                        {dimensionDetail.sizes.map((sz) => (
                          <td key={'gt-ao-' + sz} className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b' }}>
                            {detailTotals.sizeQty[sz] > 0 ? '100%' : '0%'}
                          </td>
                        ))}
                        <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--color-accent)' }}>{detailTotals.totalQty}</td>
                        <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b' }}>{detailTotals.dispatchCount}</td>
                        <td style={{ padding: '8px 4px' }} />
                        <td style={{ padding: '8px 4px' }} />
                        {selectedDimension === 'refParty' && (
                          <>
                            <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b' }}>{detailTotals.oldCustomerCount}</td>
                            <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b' }}>{detailTotals.newCustomerCount}</td>
                            <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--color-accent)' }}>{detailTotals.totalCustomerCount}</td>
                            <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--color-text-muted)' }}>{detailTotals.inactiveCustomerCount}</td>
                          </>
                        )}
                      </tr>
                    )}
                    {detailTotals && !searchActive && selectedDimension === 'refParty' && (
                      <tr style={{ background: '#f1f5f9' }}>
                        <td colSpan={2 + 2 * dimensionDetail.sizes.length + 4} style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b', textAlign: 'right' }}>
                          Unique customers (each customer counted once)
                        </td>
                        <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b' }}>{detailTotals.unique.old}</td>
                        <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b' }}>{detailTotals.unique.nw}</td>
                        <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--color-accent)' }}>{detailTotals.unique.total === null ? '—' : detailTotals.unique.total}</td>
                        <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--color-text-muted)' }}>{detailTotals.unique.inactive}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              </>
            ) : (
              rangeActive ? (
                <p style={{ color: 'var(--color-text-muted)' }}>
                  No dispatches in this date range.{' '}
                  <button type="button" onClick={() => applyRange('', '')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--color-accent)', font: 'inherit', textDecoration: 'underline' }}>Clear dates</button>
                </p>
              ) : (
                <p style={{ color: 'var(--color-text-muted)' }}>No dispatches in {monthName ? `${monthName} ` : ''}{selectedYear} for this breakdown.</p>
              )
            )}
          </div>
        )}
      </Card>

      {!focusMode && (
      <>
      <Card title="Comparison">
        {!comparisonMode && (
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <DrilldownCard label="Year-wise Comparison" onClick={() => openComparisonMode('year')} />
            <DrilldownCard label="Month-wise Comparison" onClick={() => openComparisonMode('month')} />
          </div>
        )}

        {comparisonMode && (
          <div>
            <button
              type="button"
              onClick={() => openComparisonMode(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', marginBottom: 'var(--space-3)', padding: 0 }}
            >
              ← Back
            </button>

            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', marginBottom: 8 }}>
              {comparisonMode === 'year' ? 'Add the years to compare' : 'Add the months to compare'}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}>
              {comparisonPeriods.map((p, idx) => (
                <React.Fragment key={periodKey(p)}>
                  {idx > 0 && <span style={{ fontWeight: 700, color: 'var(--color-text-muted)' }}>VS</span>}
                  <div
                    style={{
                      background: 'var(--color-surface)',
                      border: `1px solid ${periodColor(idx)}`,
                      borderLeft: `4px solid ${periodColor(idx)}`,
                      borderRadius: 'var(--radius-sm)',
                      padding: 'var(--space-2) var(--space-3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <span style={{ fontWeight: 600, color: periodColor(idx) }}>{periodLabel(p)}</span>
                    <button
                      type="button"
                      onClick={() => removeComparisonPeriod(periodKey(p))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: 12, padding: 0 }}
                    >
                      ✕
                    </button>
                  </div>
                </React.Fragment>
              ))}

              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={toggleComparisonDropdown}
                  aria-label="Add a period to compare"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    border: '1px solid var(--color-accent)',
                    background: comparisonDropdownOpen ? 'var(--color-accent)' : 'var(--color-surface)',
                    color: comparisonDropdownOpen ? '#fff' : 'var(--color-accent)',
                    fontSize: 20,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                  }}
                >
                  +
                </button>
                {comparisonDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 44,
                      left: 0,
                      zIndex: 50,
                      background: 'var(--color-surface)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-sm)',
                      padding: 'var(--space-3)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                      minWidth: 180,
                    }}
                  >
                    <select
                      value={comparisonDropdownYear}
                      onChange={(e) => handleComparisonDropdownYearChange(e.target.value)}
                      style={{ ...inputStyle, width: '100%', marginBottom: 8 }}
                    >
                      <option value="">Select year…</option>
                      {years.map((y) => (
                        <option key={y.year} value={y.year}>{y.year}</option>
                      ))}
                    </select>
                    {comparisonMode === 'month' && comparisonDropdownYear && (
                      <select
                        value={comparisonDropdownMonth}
                        onChange={(e) => setComparisonDropdownMonth(e.target.value)}
                        style={{ ...inputStyle, width: '100%', marginBottom: 8 }}
                      >
                        <option value="">Select month…</option>
                        {comparisonDropdownMonths.map((m) => (
                          <option key={m.month} value={m.month}>{m.monthName}</option>
                        ))}
                      </select>
                    )}
                    <Button
                      variant="secondary"
                      onClick={addComparisonPeriod}
                      disabled={!comparisonDropdownYear || (comparisonMode === 'month' && !comparisonDropdownMonth)}
                      style={{ width: '100%' }}
                    >
                      Add
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {comparisonPeriods.length >= 2 && (
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', marginBottom: 8 }}>
                  Select what to compare
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
                  {DRILLDOWN_DIMENSIONS.map((d) => (
                    <DrilldownCard key={d.key} label={d.label} active={comparisonDimension === d.key} onClick={() => openComparisonDimension(d.key)} />
                  ))}
                </div>
                {comparisonLoading && <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>}
              </div>
            )}

            {comparisonPeriods.length === 1 && (
              <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                Add at least one more {comparisonMode === 'year' ? 'year' : 'month'} to compare.
              </p>
            )}

            {comparisonError && <p style={{ color: 'var(--color-danger)' }}>{comparisonError}</p>}

            {comparisonData && (
              comparisonData.rows.length === 0 ? (
                <p style={{ color: 'var(--color-text-muted)' }}>No data for this comparison.</p>
              ) : (
                <div className="table-scroll">
                  <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid var(--color-border)' }}>
                    <thead>
                      <tr style={{ background: '#e2e8f0' }}>
                        <th rowSpan={2} style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', fontWeight: 700, color: '#1e293b', borderRight: '1px solid var(--color-border)', borderBottom: '1px solid var(--color-border)', verticalAlign: 'bottom' }}>
                          No.
                        </th>
                        <th rowSpan={2} style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', fontWeight: 700, color: '#1e293b', borderRight: '1px solid var(--color-border)', borderBottom: '1px solid var(--color-border)', verticalAlign: 'bottom' }}>
                          {DRILLDOWN_DIMENSIONS.find((d) => d.key === comparisonDimension).label.replace('-wise Dispatch', '')}
                        </th>
                        {comparisonData.sizes.map((s) => (
                          <th key={`g-${s}`} colSpan={comparisonData.periods.length} style={{ padding: '6px 4px', fontSize: 'var(--font-size-xs)', fontWeight: 700, color: '#1e293b', borderRight: '1px solid var(--color-border)', borderBottom: '1px solid var(--color-border)', textAlign: 'center' }}>
                            {s} All Over %
                          </th>
                        ))}
                        <th colSpan={comparisonData.periods.length} style={{ padding: '6px 4px', fontSize: 'var(--font-size-xs)', fontWeight: 700, color: '#1e293b', borderRight: '1px solid var(--color-border)', borderBottom: '1px solid var(--color-border)', textAlign: 'center' }}>
                          Total Dispatch Qty
                        </th>
                        <th colSpan={comparisonData.periods.length} style={{ padding: '6px 4px', fontSize: 'var(--font-size-xs)', fontWeight: 700, color: '#1e293b', borderBottom: '1px solid var(--color-border)', textAlign: 'center' }}>
                          Total Active Customers
                        </th>
                      </tr>
                      <tr style={{ background: '#e2e8f0' }}>
                        {comparisonData.sizes.map((s) => comparisonData.periods.map((p, pIdx) => (
                          <th
                            key={`sp-${s}-${pIdx}`}
                            className="data-figure"
                            style={{
                              padding: '6px 4px', fontSize: 'var(--font-size-xs)', fontWeight: 600, color: periodColor(pIdx),
                              borderLeft: pIdx > 0 ? '1px dashed var(--color-border)' : 'none',
                              borderRight: pIdx === comparisonData.periods.length - 1 ? '1px solid var(--color-border)' : 'none',
                              borderBottom: `3px solid ${periodColor(pIdx)}`,
                            }}
                          >
                            {periodLabel(p)}
                          </th>
                        )))}
                        {comparisonData.periods.map((p, pIdx) => (
                          <th
                            key={`tq-h-${pIdx}`}
                            className="data-figure"
                            style={{
                              padding: '6px 4px', fontSize: 'var(--font-size-xs)', fontWeight: 600, color: periodColor(pIdx),
                              borderLeft: pIdx > 0 ? '1px dashed var(--color-border)' : 'none',
                              borderRight: pIdx === comparisonData.periods.length - 1 ? '1px solid var(--color-border)' : 'none',
                              borderBottom: `3px solid ${periodColor(pIdx)}`,
                            }}
                          >
                            {periodLabel(p)}
                          </th>
                        ))}
                        {comparisonData.periods.map((p, pIdx) => (
                          <th
                            key={`ac-h-${pIdx}`}
                            className="data-figure"
                            style={{
                              padding: '6px 4px', fontSize: 'var(--font-size-xs)', fontWeight: 600, color: periodColor(pIdx),
                              borderLeft: pIdx > 0 ? '1px dashed var(--color-border)' : 'none',
                              borderBottom: `3px solid ${periodColor(pIdx)}`,
                            }}
                          >
                            {periodLabel(p)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonData.rows.map((row) => (
                        <tr key={row.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                          <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', borderRight: '1px solid var(--color-border)' }}>{row.no}</td>
                          <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', borderRight: '1px solid var(--color-border)' }}>{row.name}</td>
                          {comparisonData.sizes.map((s) => row.byPeriod.map((bp, pIdx) => (
                            <td
                              key={`sv-${s}-${pIdx}`}
                              className="data-figure"
                              style={{
                                padding: '8px 4px', fontSize: 'var(--font-size-sm)',
                                borderLeft: pIdx > 0 ? '1px dashed var(--color-border)' : 'none',
                                borderRight: pIdx === row.byPeriod.length - 1 ? '1px solid var(--color-border)' : 'none',
                              }}
                            >
                              {bp.sizeAllOverPct[s]}%
                            </td>
                          )))}
                          {row.byPeriod.map((bp, pIdx) => (
                            <td
                              key={`tq-${pIdx}`}
                              className="data-figure"
                              style={{
                                padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: periodColor(pIdx),
                                borderLeft: pIdx > 0 ? '1px dashed var(--color-border)' : 'none',
                                borderRight: pIdx === row.byPeriod.length - 1 ? '1px solid var(--color-border)' : 'none',
                              }}
                            >
                              {bp.totalDispatchQty}
                            </td>
                          ))}
                          {row.byPeriod.map((bp, pIdx) => (
                            <td
                              key={`ac-${pIdx}`}
                              className="data-figure"
                              style={{
                                padding: '8px 4px', fontSize: 'var(--font-size-sm)',
                                borderLeft: pIdx > 0 ? '1px dashed var(--color-border)' : 'none',
                              }}
                            >
                              {bp.totalActiveCustomers}
                            </td>
                          ))}
                        </tr>
                      ))}
                      {comparisonData.rows.length > 0 && (
                        <tr style={{ background: '#e2e8f0', borderTop: '2px solid var(--color-border)' }}>
                          <td style={{ padding: '8px 4px', borderRight: '1px solid var(--color-border)' }} />
                          <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b', borderRight: '1px solid var(--color-border)' }}>Grand Total</td>
                          {comparisonData.sizes.map((s) => comparisonData.periods.map((p, pIdx) => {
                            const share = comparisonData.rows.reduce((acc, r) => acc + (Number(r.byPeriod[pIdx].sizeAllOverPct[s]) || 0), 0);
                            return (
                              <td
                                key={`gt-sv-${s}-${pIdx}`}
                                className="data-figure"
                                style={{
                                  padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b',
                                  borderLeft: pIdx > 0 ? '1px dashed var(--color-border)' : 'none',
                                  borderRight: pIdx === comparisonData.periods.length - 1 ? '1px solid var(--color-border)' : 'none',
                                }}
                              >
                                {share > 0 ? '100%' : '0%'}
                              </td>
                            );
                          }))}
                          {comparisonData.periods.map((p, pIdx) => {
                            const total = comparisonData.rows.reduce((acc, r) => acc + (Number(r.byPeriod[pIdx].totalDispatchQty) || 0), 0);
                            return (
                              <td
                                key={`gt-tq-${pIdx}`}
                                className="data-figure"
                                style={{
                                  padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: periodColor(pIdx),
                                  borderLeft: pIdx > 0 ? '1px dashed var(--color-border)' : 'none',
                                  borderRight: pIdx === comparisonData.periods.length - 1 ? '1px solid var(--color-border)' : 'none',
                                }}
                              >
                                {total}
                              </td>
                            );
                          })}
                          {comparisonData.periods.map((p, pIdx) => (
                            <td
                              key={`gt-ac-${pIdx}`}
                              className="data-figure"
                              style={{
                                padding: '8px 4px', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)',
                                borderLeft: pIdx > 0 ? '1px dashed var(--color-border)' : 'none',
                              }}
                            >
                              —
                            </td>
                          ))}
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        )}
      </Card>

      <CustomerTrackingCard />

      <Card title="Dispatch Delay Analysis">
        {loading ? (
          <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>
        ) : (
          <div className="table-scroll">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-border)' }}>
                {['Design', 'Customer', 'Dispatch Date', 'Qty', 'Delay'].map((h) => (
                  <th key={h} style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {delayed.map((row) => (
                <tr key={row.dispatch_txn_id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{row.design_name}</td>
                  <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{row.customer_name}</td>
                  <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>
                    {formatDate(row.dispatch_date)}
                  </td>
                  <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{row.total}</td>
                  <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', color: 'var(--color-danger)', fontWeight: 600 }}>
                    {row.delayDays}d over ({row.targetDays}d target)
                  </td>
                </tr>
              ))}
              {delayed.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: 'var(--space-4)', color: 'var(--color-text-muted)' }}>
                    No delayed dispatches — everything shipped within the target lead time.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      <div className="responsive-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-5)' }}>
        <Card title="PRE / STD / COM / ECO Mix">
          {grades && (
            <>
              <div className="kpi-row" style={{ display: 'flex', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
                {['pre', 'std', 'com', 'eco'].map((g) => (
                  <div key={g}>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>{g}</div>
                    <div className="data-figure" style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>{grades.totals[g]}</div>
                  </div>
                ))}
              </div>
              <div className="table-scroll">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-border)' }}>
                    {['Category', 'PRE', 'STD', 'COM', 'ECO', 'Total'].map((h) => (
                      <th key={h} style={{ padding: '6px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {grades.byCategory.map((c) => (
                    <tr key={c.category} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '6px 4px', fontSize: 'var(--font-size-sm)' }}>{c.category}</td>
                      <td className="data-figure" style={{ padding: '6px 4px', fontSize: 'var(--font-size-sm)' }}>{c.pre}</td>
                      <td className="data-figure" style={{ padding: '6px 4px', fontSize: 'var(--font-size-sm)' }}>{c.std}</td>
                      <td className="data-figure" style={{ padding: '6px 4px', fontSize: 'var(--font-size-sm)' }}>{c.com}</td>
                      <td className="data-figure" style={{ padding: '6px 4px', fontSize: 'var(--font-size-sm)' }}>{c.eco}</td>
                      <td className="data-figure" style={{ padding: '6px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>{c.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </Card>

        <Card title="Dispatch Date Analysis (by day of week)">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {dayOfWeek.map((d) => (
              <div key={d.dayName} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 90, fontSize: 'var(--font-size-sm)' }}>{d.dayName}</span>
                <div style={{ flex: 1, background: 'var(--color-surface-alt)', borderRadius: 4, overflow: 'hidden', height: 16 }}>
                  <div style={{ width: `${(d.dispatchedQty / maxDayQty) * 100}%`, background: 'var(--color-accent)', height: '100%' }} />
                </div>
                <span className="data-figure" style={{ fontSize: 'var(--font-size-xs)', width: 50, textAlign: 'right' }}>{d.dispatchedQty}</span>
              </div>
            ))}
            {dayOfWeek.length === 0 && <p style={{ color: 'var(--color-text-muted)' }}>No dispatch data yet.</p>}
          </div>
        </Card>
      </div>

      <Card
        title="Dispatch Breakdown"
        actions={
          <select value={dimension} onChange={(e) => setDimension(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
            {DIMENSIONS.map((d) => (
              <option key={d.key} value={d.key}>{d.label}-wise</option>
            ))}
          </select>
        }
      >
        <div className="table-scroll">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-border)' }}>
              {['Name', 'Dispatched Qty', 'Dispatches', 'On-Time %', 'Avg Delay'].map((h) => (
                <th key={h} style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {breakdown.map((b) => (
              <tr key={b.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{b.name}</td>
                <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{b.dispatchedQty}</td>
                <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{b.dispatchCount}</td>
                <td style={{ padding: '8px 4px' }}><OnTimeBadge pct={b.onTimePct} /></td>
                <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>
                  {b.avgDelayDays === null ? '—' : `${b.avgDelayDays}d`}
                </td>
              </tr>
            ))}
            {breakdown.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: 'var(--space-4)', color: 'var(--color-text-muted)' }}>No data.</td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </Card>

      </>
      )}

      {historyCustomer && (
        <CustomerHistoryModal customer={historyCustomer} onClose={() => setHistoryCustomer(null)} />
      )}

      {customerListModal && (
        <CustomerListModal
          title={customerListModal.title}
          customers={customerListModal.customers}
          onClose={() => setCustomerListModal(null)}
        />
      )}
    </div>
  );
}
